import * as THREE from 'three/webgpu';
import {createSketchDimensionHistoryPatch} from '../../../history/dimension/SketchDimensionHistoryAdapter';
import {createExistingHistorySnapshot, createMissingHistorySnapshot} from '../../../history/types';
import type {EntityPatch} from '../../../history/types';
import type {
    SketchDimensionSketchDuplicationInput,
    SketchDimensionSketchDuplicationResult,
} from '../../../../types/sketch/dimension/SketchGroupDuplicationTypes';
import {SketchDimension} from '../model/SketchDimension';
import {SketchDimensionIdentityService} from '../repository/SketchDimensionIdentityService';
import type {
    SketchDimensionReferenceRecord,
    SketchDimensionSnapshot,
} from '../SketchDimensionTypes';

/**
 * 整草图智能尺寸复制服务。
 *
 * 设计说明：
 * 1. 设计树“复制草图”会复制草图内全部智能尺寸，不复用快捷键剪贴板的显式选择状态；
 * 2. 新尺寸、参数和尺寸链分组全部使用新身份，源草图与目标草图之间不保留可变共享状态；
 * 3. 草图对象引用只通过本次调用传入的临时持久 ID 映射转换，该映射不会写入领域仓库；
 * 4. 本服务只负责尺寸仓库和尺寸历史补丁，草图组克隆、注册及历史事务提交由上层协调。
 */
export class SketchDimensionSketchDuplicationService {
    /** 尺寸身份分配服务。 */
    private readonly identityService: SketchDimensionIdentityService;

    /**
     * @param identityService 可注入的尺寸身份分配服务。
     */
    public constructor(identityService = new SketchDimensionIdentityService()) {
        this.identityService = identityService;
    }

    /**
     * 把源草图的全部智能尺寸复制到新草图。
     *
     * @param input 尺寸仓库、草图身份和对象身份映射。
     * @returns 新尺寸身份与对应的创建历史补丁。
     */
    public duplicate(input: SketchDimensionSketchDuplicationInput): SketchDimensionSketchDuplicationResult {
        const sourceSnapshots = input.repository.listBySketch(input.sourceSketchId)
            .map((dimension) => dimension.toSnapshot());

        if (sourceSnapshots.length === 0) {
            return {dimensionIds: [], dimensionPatches: []};
        }

        const occupiedNames = new Set(
            input.repository.listBySketch(input.targetSketchId).map((dimension) => dimension.currentName),
        );
        const groupIdMap = new Map<string, string>();
        const createdSnapshots: SketchDimensionSnapshot[] = [];
        const dimensionPatches: EntityPatch[] = [];

        try {
            sourceSnapshots.forEach((sourceSnapshot) => {
                const snapshot = this.createTargetSnapshot(
                    sourceSnapshot,
                    input,
                    occupiedNames,
                    groupIdMap,
                );

                // 仓库创建是领域主状态变更；补丁必须使用同一份不可变快照，保证 Undo/Redo 精确往返。
                input.repository.create(SketchDimension.fromSnapshot(snapshot));
                createdSnapshots.push(snapshot);
                dimensionPatches.push(createSketchDimensionHistoryPatch({
                    entityId: snapshot.dimensionId,
                    operation: 'create',
                    before: createMissingHistorySnapshot(),
                    after: createExistingHistorySnapshot(snapshot),
                    metadata: {sketchId: input.targetSketchId},
                }));
            });
        } catch (error) {
            // 整草图复制必须原子：任一尺寸失败时撤销本批已创建尺寸，再由上层回滚草图组。
            createdSnapshots.forEach((snapshot) => {
                input.repository.removeDimensionForRestore(snapshot.dimensionId);
            });
            throw error;
        }

        return {
            dimensionIds: createdSnapshots.map((snapshot) => snapshot.dimensionId),
            dimensionPatches,
        };
    }

    /**
     * 创建一条与源尺寸完全解耦的目标快照。
     *
     * @param sourceSnapshot 源尺寸快照。
     * @param input 整草图复制输入。
     * @param occupiedNames 目标草图已占用名称。
     * @param groupIdMap 源尺寸链分组到目标分组的临时映射。
     * @returns 可直接写入目标仓库的新尺寸快照。
     */
    private createTargetSnapshot(
        sourceSnapshot: SketchDimensionSnapshot,
        input: SketchDimensionSketchDuplicationInput,
        occupiedNames: Set<string>,
        groupIdMap: Map<string, string>,
    ): SketchDimensionSnapshot {
        const dimensionId = this.identityService.allocateDimensionId();
        const name = occupiedNames.has(sourceSnapshot.name)
            ? this.identityService.allocateName(occupiedNames)
            : sourceSnapshot.name;
        const sourceGroupId = sourceSnapshot.branch.chainGroupId;
        let targetGroupId: string | null = null;

        if (sourceGroupId) {
            targetGroupId = groupIdMap.get(sourceGroupId) ?? THREE.MathUtils.generateUUID();
            groupIdMap.set(sourceGroupId, targetGroupId);
        }

        /**
         * 求解种子属于源 solver island，不能跨草图复用；尺寸链分组则在本次复制内统一换新，
         * 保证同一组成员仍共享目标分组身份。
         */
        const {
            chainGroupId: _sourceGroupId,
            solveSeedId: _sourceSolveSeedId,
            ...stableBranch
        } = sourceSnapshot.branch;
        const snapshot: SketchDimensionSnapshot = {
            ...sourceSnapshot,
            dimensionId,
            sketchId: input.targetSketchId,
            name,
            parameter: {
                ...sourceSnapshot.parameter,
                parameterId: this.identityService.createParameterId(
                    dimensionId,
                    sourceSnapshot.parameter.quantityKind,
                ),
                // 新逻辑草图不继承可能指向源草图局部名称的表达式，只保留已求值的名义值。
                expression: null,
            },
            references: sourceSnapshot.references.map((reference) => (
                this.remapReference(reference, input.persistentIdMap)
            )),
            branch: {
                ...stableBranch,
                ...(targetGroupId ? {chainGroupId: targetGroupId} : {}),
            },
            display: {...sourceSnapshot.display},
            solveState: 'unknown',
            repair: null,
        };

        occupiedNames.add(name);
        return snapshot;
    }

    /**
     * 把一条尺寸引用从源草图身份映射到目标草图身份。
     *
     * @param reference 源尺寸引用。
     * @param persistentIdMap 源对象到目标对象的持久 ID 映射。
     * @returns 目标尺寸引用。
     */
    private remapReference(
        reference: SketchDimensionReferenceRecord,
        persistentIdMap: ReadonlyMap<string, string>,
    ): SketchDimensionReferenceRecord {
        const entityId = persistentIdMap.get(reference.entityId);
        const pointId = reference.pointId ? persistentIdMap.get(reference.pointId) : undefined;

        if (!entityId && !reference.externalSourcePath) {
            throw new Error(`复制草图尺寸失败：未找到实体持久 ID 映射 ${reference.entityId}。`);
        }
        if (reference.pointId && !pointId && !reference.externalSourcePath) {
            throw new Error(`复制草图尺寸失败：未找到点持久 ID 映射 ${reference.pointId}。`);
        }

        return {
            ...reference,
            // 外部模型引用不属于草图复制域，继续指向同一稳定外部来源。
            entityId: entityId ?? reference.entityId,
            ...(reference.pointId ? {pointId: pointId ?? reference.pointId} : {}),
        };
    }
}
